import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
  Fab,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

interface User {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface UserManagementProps {
  currentUser: User;
  themeMode?: 'light' | 'dark';
}

const UserManagement: React.FC<UserManagementProps> = ({ currentUser, themeMode = 'dark' }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Create user dialog state
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: 'user',
    isActive: true
  });
  const [createUserError, setCreateUserError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Edit user dialog state
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [editingUserData, setEditingUserData] = useState({
    id: '',
    username: '',
    firstName: '',
    lastName: '',
    role: 'user',
    isActive: true
  });
  const [editUserPassword, setEditUserPassword] = useState('');
  const [editUserConfirmPassword, setEditUserConfirmPassword] = useState('');
  const [editUserError, setEditUserError] = useState('');

  // Delete user dialog state
  const [deleteUserDialog, setDeleteUserDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleteUserError, setDeleteUserError] = useState('');

  const isAdmin = currentUser.role === 'admin';

  // Auto-dismiss success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      
      if (isAdmin) {
        // Admin can see all users
        const response = await apiGet('/prod/user-management');
        
        if (response.error) {
          throw new Error(response.error);
        }
        
        setUsers(response.data?.users || []);
      } else {
        // Regular user can only see themselves
        setUsers([currentUser]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, currentUser]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEditUser = (user: User) => {
    setEditingUserData({
      id: user.id,
      username: user.username,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      isActive: user.isActive
    });
    setEditUserPassword('');
    setEditUserConfirmPassword('');
    setEditUserDialog(true);
    setEditUserError('');
  };

  const handleSaveUserEdit = async () => {
    // Validate form
    if (!editingUserData.username || !editingUserData.firstName || !editingUserData.lastName) {
      setEditUserError('Username, first name, and last name are required');
      return;
    }
    
    if (editUserPassword && editUserPassword !== editUserConfirmPassword) {
      setEditUserError('Passwords do not match');
      return;
    }
    
    if (editUserPassword && editUserPassword.length < 6) {
      setEditUserError('Password must be at least 6 characters');
      return;
    }

    try {
      const updateData: any = {
        id: editingUserData.id,
        username: editingUserData.username,
        firstName: editingUserData.firstName,
        lastName: editingUserData.lastName,
        role: editingUserData.role,
        isActive: editingUserData.isActive
      };

      // Only include password if it was provided
      if (editUserPassword) {
        updateData.password = editUserPassword;
      }

      const response = await apiPut('/prod/user-management', updateData);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      setEditUserDialog(false);
      setEditUserError('');
      setEditUserPassword('');
      setEditUserConfirmPassword('');
      setSuccessMessage('User updated successfully');
      
      // Refresh user list
      await fetchUsers();
    } catch (err: any) {
      setEditUserError(err.message || 'Failed to update user');
    }
  };

  const closeEditUserDialog = () => {
    setEditUserDialog(false);
    setEditUserError('');
    setEditUserPassword('');
    setEditUserConfirmPassword('');
    setEditingUserData({
      id: '',
      username: '',
      firstName: '',
      lastName: '',
      role: 'user',
      isActive: true
    });
  };

  const refreshUserList = useCallback(async (createdUsername: string, retryCount = 0, maxRetries = 5) => {
    try {
      setRefreshing(true);
      await fetchUsers();
      
      // Check if the new user appears in the list
      if (isAdmin && retryCount < maxRetries) {
        const response = await apiGet('/prod/user-management');
        
        if (!response.error) {
          const userExists = response.data?.users?.some((u: any) => u.username === createdUsername);
          
          if (!userExists) {
            // Wait 1 second before retrying
            setTimeout(() => {
              refreshUserList(createdUsername, retryCount + 1, maxRetries);
            }, 1000);
            return;
          }
        }
      }
      
      setRefreshing(false);
    } catch (error) {
      setRefreshing(false);
      if (retryCount < maxRetries) {
        setTimeout(() => {
          refreshUserList(createdUsername, retryCount + 1, maxRetries);
        }, 1000);
      } else {
        setError('User created but failed to refresh list. Please refresh the page.');
      }
    }
  }, [fetchUsers, isAdmin]);

  const handleCreateUser = async () => {
    // Validate form
    if (!newUser.username || !newUser.password || !newUser.firstName || !newUser.lastName) {
      setCreateUserError('All fields are required');
      return;
    }
    
    if (newUser.password !== newUser.confirmPassword) {
      setCreateUserError('Passwords do not match');
      return;
    }
    
    if (newUser.password.length < 6) {
      setCreateUserError('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await apiPost('/prod/user-management', {
        username: newUser.username,
        password: newUser.password,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        role: newUser.role,
        isActive: newUser.isActive
      });
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      // Store the username before resetting the form
      const createdUsername = newUser.username;
      
      // Reset form and close dialog
      setNewUser({
        username: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        role: 'user',
        isActive: true
      });
      setCreateUserDialog(false);
      setCreateUserError('');
      setSuccessMessage('User created successfully');
      
      // Refresh user list with retry mechanism
      refreshUserList(createdUsername);
    } catch (err: any) {
      setCreateUserError(err.message || 'Failed to create user');
    }
  };

  const openCreateUserDialog = () => {
    setCreateUserDialog(true);
    setCreateUserError('');
    setNewUser({
      username: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      role: 'user',
      isActive: true
    });
  };

  const closeCreateUserDialog = () => {
    setCreateUserDialog(false);
    setCreateUserError('');
    setNewUser({
      username: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      role: 'user',
      isActive: true
    });
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    // Prevent users from deleting themselves
    if (userToDelete.id === currentUser.id) {
      setDeleteUserError('You cannot delete your own account. Please contact an administrator.');
      return;
    }

    try {
      const response = await apiDelete('/prod/user-management', {
        id: userToDelete.id
      });
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      setDeleteUserDialog(false);
      setUserToDelete(null);
      setDeleteUserError('');
      setSuccessMessage('User deleted successfully');
      
      // Refresh user list
      await fetchUsers();
    } catch (err: any) {
      setDeleteUserError(err.message || 'Failed to delete user');
    }
  };

  const openDeleteUserDialog = (user: User) => {
    setUserToDelete(user);
    setDeleteUserDialog(true);
    setDeleteUserError('');
  };

  const closeDeleteUserDialog = () => {
    setDeleteUserDialog(false);
    setUserToDelete(null);
    setDeleteUserError('');
  };

  const displayUsers = isAdmin ? users : [currentUser];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <Typography>Loading users...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: themeMode === 'light' ? '#333' : 'white' }}>
          {isAdmin ? 'User Management' : 'My Profile'}
        </Typography>
        {isAdmin && (
          <Tooltip title="Add New User">
            <Fab 
              color="primary" 
              size="small"
              sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
              onClick={openCreateUserDialog}
            >
              <AddIcon />
            </Fab>
          </Tooltip>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setSuccessMessage('')}
            >
              <CancelIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {successMessage}
          {refreshing && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="body2">Updating user list...</Typography>
            </Box>
          )}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ 
        bgcolor: themeMode === 'light' ? 'white' : '#232323', 
        position: 'relative',
        boxShadow: themeMode === 'light' 
          ? '0 4px 20px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)' 
          : '0 4px 20px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.3)'
      }}>
        {refreshing && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <Box sx={{ textAlign: 'center', color: 'white' }}>
              <CircularProgress size={40} sx={{ mb: 1 }} />
              <Typography variant="body2">Updating user list...</Typography>
            </Box>
          </Box>
        )}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                color: themeMode === 'light' ? '#333' : 'white',
                borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                fontWeight: 'bold',
                padding: '8px 16px',
                fontSize: '0.875rem'
              }}>Name</TableCell>
              <TableCell sx={{ 
                color: themeMode === 'light' ? '#333' : 'white',
                borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                fontWeight: 'bold',
                padding: '8px 16px',
                fontSize: '0.875rem'
              }}>Email</TableCell>
              <TableCell sx={{ 
                color: themeMode === 'light' ? '#333' : 'white',
                borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                fontWeight: 'bold',
                padding: '8px 16px',
                fontSize: '0.875rem'
              }}>Role</TableCell>
              <TableCell sx={{ 
                color: themeMode === 'light' ? '#333' : 'white',
                borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                fontWeight: 'bold',
                padding: '8px 16px',
                fontSize: '0.875rem'
              }}>Status</TableCell>
              {isAdmin && <TableCell sx={{ 
                color: themeMode === 'light' ? '#333' : 'white',
                borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                fontWeight: 'bold',
                padding: '8px 16px',
                fontSize: '0.875rem'
              }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayUsers.map((user) => (
              <TableRow key={user.id} sx={{
                '&:hover': {
                  bgcolor: themeMode === 'light' ? '#f0f0f0' : 'rgba(255, 255, 255, 0.08)'
                }
              }}>
                <TableCell sx={{ 
                  color: themeMode === 'light' ? '#333' : 'white',
                  borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                  padding: '8px 16px',
                  fontSize: '0.875rem'
                }}>
                  {user.firstName} {user.lastName}
                </TableCell>
                <TableCell sx={{ 
                  color: themeMode === 'light' ? '#333' : 'white',
                  borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                  padding: '8px 16px',
                  fontSize: '0.875rem'
                }}>{user.username}</TableCell>
                <TableCell sx={{
                  borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                  padding: '8px 16px'
                }}>
                  <Chip 
                    label={user.role} 
                    size="small"
                    sx={{ 
                      bgcolor: user.role === 'admin' ? '#d32f2f' : '#2196f3',
                      color: 'white',
                      height: '24px',
                      fontSize: '0.75rem'
                    }}
                  />
                </TableCell>
                <TableCell sx={{
                  borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                  padding: '8px 16px'
                }}>
                  <Chip 
                    label={user.isActive ? 'Active' : 'Inactive'} 
                    size="small"
                    sx={{ 
                      bgcolor: user.isActive ? '#4caf50' : '#9e9e9e',
                      color: 'white',
                      height: '24px',
                      fontSize: '0.75rem'
                    }}
                  />
                </TableCell>
                {isAdmin && (
                  <TableCell sx={{
                    borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                    padding: '8px 16px'
                  }}>
                    <IconButton 
                      onClick={() => handleEditUser(user)}
                      sx={{ color: '#2196f3', mr: 1 }}
                      title="Edit User"
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {user.id !== currentUser.id && (
                      <IconButton 
                        onClick={() => openDeleteUserDialog(user)}
                        sx={{ color: '#f44336' }}
                        title="Delete User"
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                )}
                {!isAdmin && user.id === currentUser.id && (
                  <TableCell sx={{
                    borderBottom: themeMode === 'light' ? '1px solid #ccc' : '1px solid #444',
                    padding: '8px 16px'
                  }}>
                    <IconButton 
                      onClick={() => handleEditUser(user)}
                      sx={{ color: '#2196f3' }}
                      title="Edit Profile"
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={editUserDialog} onClose={closeEditUserDialog}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="First Name"
            type="text"
            fullWidth
            variant="outlined"
            value={editingUserData.firstName}
            onChange={(e) => setEditingUserData({ ...editingUserData, firstName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Last Name"
            type="text"
            fullWidth
            variant="outlined"
            value={editingUserData.lastName}
            onChange={(e) => setEditingUserData({ ...editingUserData, lastName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={editingUserData.username}
            onChange={(e) => setEditingUserData({ ...editingUserData, username: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Role"
            select
            fullWidth
            variant="outlined"
            value={editingUserData.role}
            onChange={(e) => setEditingUserData({ ...editingUserData, role: e.target.value })}
            disabled={editingUserData.id === currentUser.id}
            sx={{ mb: 2 }}
          >
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={editingUserData.isActive}
                onChange={(e) => setEditingUserData({ ...editingUserData, isActive: e.target.checked })}
                disabled={editingUserData.id === currentUser.id}
              />
            }
            label="Active"
          />
          <TextField
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            variant="outlined"
            value={editUserPassword}
            onChange={(e) => setEditUserPassword(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Confirm New Password"
            type="password"
            fullWidth
            variant="outlined"
            value={editUserConfirmPassword}
            onChange={(e) => setEditUserConfirmPassword(e.target.value)}
            error={!!editUserError}
            helperText={editUserError}
          />
          {editUserError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {editUserError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditUserDialog}>Cancel</Button>
          <Button onClick={handleSaveUserEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserDialog} onClose={closeCreateUserDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={newUser.username}
            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="First Name"
            fullWidth
            variant="outlined"
            value={newUser.firstName}
            onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Last Name"
            fullWidth
            variant="outlined"
            value={newUser.lastName}
            onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Password"
            type="password"
            fullWidth
            variant="outlined"
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Confirm Password"
            type="password"
            fullWidth
            variant="outlined"
            value={newUser.confirmPassword}
            onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label="Role"
            fullWidth
            variant="outlined"
            value={newUser.role}
            onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
            sx={{ mb: 2 }}
          >
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={newUser.isActive}
                onChange={(e) => setNewUser({ ...newUser, isActive: e.target.checked })}
              />
            }
            label="Active"
          />
          {createUserError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {createUserError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateUserDialog}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteUserDialog} onClose={closeDeleteUserDialog}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          {deleteUserError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteUserError}
            </Alert>
          )}
          <Typography>
            Are you sure you want to delete this user? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteUserDialog}>Cancel</Button>
          <Button onClick={handleDeleteUser} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement; 